/*
 * Which routes this visitor has seen, kept in sessionStorage['ng-visited'].
 * It is a convenience: if storage is unavailable (private mode, blocked site
 * data) every access fails silently and visits live only in memory.
 */
import { createContext, createElement, useCallback, useContext, useEffect, useMemo, useState } from 'react';

const STORAGE_KEY = 'ng-visited';

function readVisited() {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(parsed) ? parsed.filter((r) => typeof r === 'string') : []);
  } catch {
    return new Set();
  }
}

function writeVisited(visited) {
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify([...visited]));
  } catch {
    // Storage unavailable: keep the in-memory copy only.
  }
}

const VisitedContext = createContext({ visited: new Set(), markVisited: () => {} });

export function VisitedProvider({ children }) {
  const [visited, setVisited] = useState(readVisited);

  const markVisited = useCallback((route) => {
    setVisited((prev) => {
      if (prev.has(route)) return prev;
      const next = new Set(prev);
      next.add(route);
      return next;
    });
  }, []);

  useEffect(() => {
    writeVisited(visited);
  }, [visited]);

  const value = useMemo(() => ({ visited, markVisited }), [visited, markVisited]);
  return createElement(VisitedContext.Provider, { value }, children);
}

export function useVisited() {
  return useContext(VisitedContext);
}
